/**
 * DeckChat — the one way to work with Lykke on a deck (2026-09-30).
 *
 * Like Claude Code: you say what you want, she does it on the deck, you look and say what to change. A new deck
 * from one sentence, a change to one page or all of them, a new page, a reorder, a question, "present it" — all
 * here. The editor stays for hands-on edits (click to type, drag); everything else is a message.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Copy, MessageSquarePlus, Phone, RotateCcw, Sparkle, Volume2, VolumeX, X } from 'lucide-react';
import { AgentComposer, type AgentComposerHandle, type CallPhase } from '@/cowork/shell/AgentComposer';
import { coworkApi, type DeckChatEvent } from '@/cowork/omniad/api/coworkApi';
import { RealtimeCall, type RealtimeTool } from '@/cowork/proposals/realtimeCall';
import { voiceRefusalText } from '@/cowork/voice/voiceSession';
import type { DeckMark } from './stageMarks';

/** Pictures attached in the composer (paperclip, paste, drop) go up at once; up to this many per message. */
const MAX_PICS = 24;

export interface ChatTurn { role: 'user' | 'assistant'; text: string; at?: string; /** Said on a call. */ voice?: boolean; decisions?: Array<{ question: string; options: string[] }>; /** Came from the saved history (not typed in this view). */ saved?: boolean; /** Pictures sent with it. */ pictures?: string[]; /** The page a part of was marked (1-based). */ marked?: number }
/** The open choices: those of Lykke's last reply, when nothing was said after it. */
const openDecisions = (turns: ChatTurn[]) => { const last = turns[turns.length - 1]; return last?.role === 'assistant' ? last.decisions || [] : []; };

const KIND_WORD: Record<string, string> = { results: 'Wrap-up', proposal: 'Proposal', creators: 'Creator deck', monthly: 'Monthly deck', showcase: 'Showcase', case: 'Case study' };


/** Lykke's words with the little formatting she uses: **bold** and "- " bullets. Text only — no HTML is injected. */
export function Said({ text }: { text: string }) {
  const inline = (line: string) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <strong key={i} className="font-semibold text-[#1c1917]">{part.slice(2, -2)}</strong> : <span key={i}>{part}</span>));
  const blocks: Array<{ bullet: boolean; lines: string[] }> = [];
  for (const raw of text.split('\n')) {
    const line = raw.trimEnd(); if (!line.trim()) { blocks.push({ bullet: false, lines: [] }); continue; }
    const bullet = /^\s*[-•*]\s+/.test(line);
    const last = blocks[blocks.length - 1];
    if (last && last.bullet === bullet && last.lines.length) last.lines.push(line.replace(/^\s*[-•*]\s+/, '')); else blocks.push({ bullet, lines: [line.replace(/^\s*[-•*]\s+/, '')] });
  }
  return (
    <div className="space-y-2.5">
      {blocks.filter((b) => b.lines.length).map((b, i) => (b.bullet
        ? <ul key={i} className="list-disc space-y-1 pl-5 marker:text-[#a8a29e]">{b.lines.map((l, j) => <li key={j}>{inline(l)}</li>)}</ul>
        : <p key={i}>{b.lines.map((l, j) => <span key={j}>{inline(l)}{j < b.lines.length - 1 ? <br /> : null}</span>)}</p>))}
    </div>
  );
}

/** What went wrong, in words a person can act on (the raw message stays in the console). */
function friendlyError(raw: string): string {
  console.warn('[deck-chat]', raw);
  if (/still working on this deck/i.test(raw)) return 'Lykke is still on another message for this deck: try again when she is done.';
  if (/overloaded|529|rate.?limit|429|too many requests/i.test(raw)) return 'Claude is very busy right now. Try again in a minute: nothing was lost.';
  if (/failed to fetch|network|load failed|ECONNRESET|socket/i.test(raw)) return 'The connection dropped. Pages that were already written are saved: send it again to finish.';
  if (/another workspace|not shared/i.test(raw)) return raw;
  // Internals (parser positions, stack text) mean nothing to a colleague: they stay in the console above.
  return 'Lykke stopped before she finished. The pages already written are saved: send it again to finish.';
}

export function DeckChat({ presentationId, index, pageCount, brandName, initialAsk, onInitialAskUsed, externalAsk, onBeforeRun, onRunStart, onEvent, onRunEnd, readiness, onReview, onShare, mark, onClearMark, onStartMark, captureView }: {
  presentationId: string;
  index: number;
  pageCount: number;
  brandName?: string;
  brandId?: string;
  /** A message to send as soon as the chat opens (a deck started from Deck studio's box). */
  initialAsk?: string | null;
  onInitialAskUsed?: () => void;
  /** An instruction from Lykke the guide (top bar): sent as if typed here, once per `n`. */
  externalAsk?: { text: string; n: number } | null;
  /** Save pending edits before Lykke reads the deck. */
  onBeforeRun: () => Promise<void>;
  /** The page on screen as a picture (what the person is looking at), sent with each message so Lykke sees it too. */
  captureView?: () => Promise<string | null>;
  onRunStart: () => void;
  onEvent: (ev: DeckChatEvent) => void;
  onRunEnd: (changed: boolean) => void;
  /** The fact check after a build: running, how many figures still need a look, or clean. */
  readiness?: { running: boolean; open: number; clean: boolean; verified?: number; figures?: number };
  onReview?: () => void;
  onShare?: () => void;
  /** Part of a page the person marked on the slide: it goes with the next message. */
  mark?: DeckMark | null;
  onClearMark?: () => void;
  /** "+" → Mark part of the page: the editor turns on marking. */
  onStartMark?: () => void;
}) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  // Choices only the person can make ("which budget?"), one tap each — shown under Lykke's latest reply.
  const [decisions, setDecisions] = useState<Array<{ question: string; options: string[] }>>([]);
  // A deck built from nothing ends with a 'ready' card: what it is, how many pages, and the next step one tap away.
  const [ready, setReady] = useState<{ kind?: string; pages: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<AgentComposerHandle>(null);
  const askedRef = useRef(false);
  // A message of ours is on its way: 'working' on the server is us, not someone else.
  const inFlightRef = useRef(false);
  const aliveRef = useRef(true);
  const waitingRef = useRef(false);
  // A fresh mark: the box is ready for what should change there.
  useEffect(() => { if (mark) setTimeout(() => inputRef.current?.focus(), 0); }, [mark]);
  /** Pictures go up as they are attached (10 per call; each answer matched by name, then by order). */
  const uploadPictures = async (files: File[]): Promise<Array<string | null>> => {
    const out: Array<string | null> = [];
    for (let k = 0; k < files.length; k += 10) {
      const part = files.slice(k, k + 10);
      try {
        const r = await coworkApi.presentations.uploadMedia(part);
        const left = [...(r.files || [])];
        for (const f of part) { const j = left.findIndex((x) => x.name === f.name); out.push(j >= 0 ? left.splice(j, 1)[0].url : left.shift()?.url || null); }
      } catch { for (let i = 0; i < part.length; i++) out.push(null); }
    }
    return out;
  };

  useEffect(() => {
    let cancelled = false; aliveRef.current = true;
    // History goes above anything already said in this session (a first message can be on its way already).
    coworkApi.presentations.chatHistory(presentationId).then((r) => {
      if (cancelled) return;
      // Saved turns replace saved turns (a remount never doubles them); what was said in this view since stays after.
      setTurns((cur) => [...(r.turns || []).map((t) => ({ ...t, saved: true })), ...cur.filter((t) => !t.saved)]);
      if (!inFlightRef.current) setDecisions(openDecisions(r.turns || []));
      // Lykke is mid-message on this deck (another tab, another person): wait with her, then catch up.
      if (r.working && !inFlightRef.current) void waitForOther();
    }).catch(() => undefined);
    // The editor hit 'Lykke is working' on save: wait with her right away.
    const onBusy = (e: Event) => { if ((e as CustomEvent<string>).detail === presentationId && !inFlightRef.current) void waitForOther(); };
    window.addEventListener('deck-chat-busy', onBusy);
    // While idle, notice a message someone else started on this deck (another tab, a colleague) and wait with it.
    const idle = setInterval(() => {
      if (inFlightRef.current || waitingRef.current || document.hidden) return;
      void coworkApi.presentations.chatHistory(presentationId).then((r) => { if (r.working && !inFlightRef.current && !waitingRef.current && aliveRef.current) void waitForOther(); }).catch(() => undefined);
    }, 15000);
    // Leaving does not stop Lykke: she finishes and the deck is saved on the server either way.
    return () => { cancelled = true; aliveRef.current = false; clearInterval(idle); window.removeEventListener('deck-chat-busy', onBusy); };
  }, [presentationId]);
  // The editor's review flow asks Lykke to fix a figure through here.
  useEffect(() => {
    const onAsk = (e: Event) => { const d = (e as CustomEvent<{ id: string; text: string; origin?: 'review'; target?: DeckMark }>).detail; if (d?.id === presentationId) void sendRef.current?.(d.text, d.origin ? { origin: d.origin } : d.target ? { target: d.target } : {}); };
    window.addEventListener('deck-chat-send', onAsk); return () => window.removeEventListener('deck-chat-send', onAsk);
  }, [presentationId]);
  useEffect(() => { const el = scrollRef.current; if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }, [turns, status]);

  const waitForOther = async () => {
    if (waitingRef.current || inFlightRef.current) return;
    waitingRef.current = true;
    setBusy(true); setStatus('Lykke is working on this deck (started elsewhere)'); onRunStart();
    try {
      for (let i = 0; i < 200 && aliveRef.current; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        if (!aliveRef.current) return;
        const r = await coworkApi.presentations.chatHistory(presentationId).catch(() => null);
        if (r && !r.working) { setTurns((r.turns || []).map((t) => ({ ...t, saved: true }))); setDecisions(openDecisions(r.turns || [])); break; }
      }
    } finally {
      waitingRef.current = false;
      if (aliveRef.current) { setBusy(false); setStatus(''); onRunEnd(true); }
    }
  };

  const sendRef = useRef<((m: string, o?: { voice?: boolean; origin?: 'review'; box?: boolean; target?: DeckMark; pictures?: Array<{ url: string; name: string }> }) => Promise<string>) | null>(null);
  const send = async (message: string, opts: { voice?: boolean; origin?: 'review'; /** Typed in the box: carries the mark on the slide. */ box?: boolean; /** Asked next to a part of a page (AskHere): that part is the target. */ target?: DeckMark; /** Pictures attached in the box (uploaded). */ pictures?: Array<{ url: string; name: string }> } = {}): Promise<string> => {
    const text = message.trim(); if (!text || inFlightRef.current || (busy && !opts.voice)) return '';
    // What goes with a message from the box: the mark on the slide and the pictures attached. The app's own messages
    // (figure review, the guide, a deck started elsewhere) and calls go as they are.
    const withMark = opts.target || (opts.box ? mark || null : null);
    const sentPics = opts.box ? opts.pictures || [] : [];
    let said = '';
    inFlightRef.current = true;
    setBusy(true); setStatus('Reading the deck'); setDecisions([]); setReady(null);
    const fresh = pageCount <= 1;
    let built: { kind?: string; pages: number } | null = null;
    setTurns((t) => [...t, { role: 'user', text, voice: opts.voice, ...(sentPics.length ? { pictures: sentPics.map((p) => p.url) } : {}), ...(withMark ? { marked: withMark.index + 1 } : {}) }, { role: 'assistant', text: '' }]);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    let changed = false;
    try {
      await onBeforeRun();
      // What they are looking at (the page on screen), at most ~1 s to capture; without it she still has the page's content.
      const view = captureView ? await Promise.race([captureView().catch(() => null), new Promise<null>((r) => setTimeout(() => r(null), 1200))]) : null;
      onRunStart();
      await coworkApi.presentations.chat(presentationId, { message: text, index: withMark ? withMark.index : index, ...(view ? { view } : {}), ...(opts.origin ? { origin: opts.origin } : {}), ...(withMark ? { target: { index: withMark.index, region: withMark.region, elementIds: withMark.elementIds, texts: withMark.texts, ...(withMark.path ? { path: withMark.path } : {}) } } : {}), ...(sentPics.length ? { attachments: sentPics.map((p) => ({ url: p.url, name: p.name })) } : {}) }, (ev) => {
        if (ev.event === 'status') setStatus(ev.label);
        else if (ev.event === 'text') { said += ev.delta; setTurns((t) => { const n = t.slice(); const last = n[n.length - 1]; n[n.length - 1] = { ...last, text: last.text + ev.delta }; return n; }); }
        else if (ev.event === 'page' && ev.state === 'writing') setStatus((s) => (s.startsWith('Writing') || s.startsWith('Updating') ? s : 'Composing the pages'));
        else if (ev.event === 'decisions') setDecisions(ev.items || []);
        else if (ev.event === 'done') changed = ev.changed;
        if (ev.event === 'deck') { changed = true; built = { kind: (ev as { kind?: string }).kind, pages: ev.slides.filter((sl) => !(sl as { _pending?: unknown })._pending).length }; }
        onEvent(ev);
      }, ctrl.signal);
    } catch (e) {
      const msg = (e as Error).name === 'AbortError' ? 'Stopped.' : friendlyError((e as Error).message);
      setTurns((t) => { const n = t.slice(); const last = n[n.length - 1]; n[n.length - 1] = { ...last, text: last.text || msg }; return n; });
    } finally {
      setTurns((t) => { const n = t.slice(); const last = n[n.length - 1]; if (last?.role === 'assistant' && !last.text) n[n.length - 1] = { ...last, text: changed ? 'Done.' : 'No change.' }; return n; });
      inFlightRef.current = false;
      setBusy(false); setStatus(''); abortRef.current = null;
      if (withMark) onClearMark?.();
      const b = built as { kind?: string; pages: number } | null; // set inside the stream callback
      if (fresh && changed && b && b.pages > 1 && !ctrl.signal.aborted && aliveRef.current) setReady(b);
      onRunEnd(changed);
      if (!opts.voice) setTimeout(() => inputRef.current?.focus(), 0);
    }
    return said || (changed ? 'Done.' : 'No change.');
  };

  // Lykke the guide passed an instruction for this deck: it goes out like a typed message.
  const externalRef = useRef(0);
  useEffect(() => {
    if (externalAsk && externalAsk.n !== externalRef.current) { externalRef.current = externalAsk.n; void send(externalAsk.text); }
  }, [externalAsk]);

  // A deck started from Deck studio's box: its first message goes out as soon as the chat is up.
  useEffect(() => {
    if (initialAsk && !askedRef.current) { askedRef.current = true; onInitialAskUsed?.(); void send(initialAsk); }
  }, [initialAsk]);

  const empty = pageCount <= 1;

  // ── Read aloud (the browser's own voice: nothing leaves the device) ──
  const [speaking, setSpeaking] = useState<number | null>(null);
  const readAloud = (i: number, text: string) => {
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined; if (!synth) return;
    synth.cancel(); if (speaking === i) { setSpeaking(null); return; }
    const u = new SpeechSynthesisUtterance(text.replace(/\*\*/g, '').replace(/^\s*[-•*]\s+/gm, ''));
    u.rate = 1.08; u.onend = () => setSpeaking(null); u.onerror = () => setSpeaking(null);
    setSpeaking(i); synth.speak(u);
  };
  useEffect(() => () => window.speechSynthesis?.cancel(), []);
  const [copied, setCopied] = useState<number | null>(null);
  const copy = (i: number, text: string) => { void navigator.clipboard?.writeText(text).then(() => { setCopied(i); setTimeout(() => setCopied(null), 1400); }); };
  const lastAsk = (i: number) => { for (let j = i - 1; j >= 0; j--) if (turns[j].role === 'user') return turns[j].text; return ''; };

  // ── Call: talk with Lykke. Her voice (OpenAI Realtime) hears you and hands every request to the same deck agent
  // as the chat, so a call is this conversation spoken: the asks and her replies land here as turns. ──
  const callRef = useRef<RealtimeCall | null>(null);
  const [call, setCall] = useState<{ phase: 'connecting' | 'listening' | 'speaking' | 'working' | 'error'; caption?: string; heard?: string; error?: string; muted?: boolean } | null>(null);
  const patchCall = (p: Partial<NonNullable<typeof call>>) => setCall((c) => (c ? { ...c, ...p } : c));
  const indexRefCall = useRef(index); indexRefCall.current = index;
  const pagesRefCall = useRef(pageCount); pagesRefCall.current = pageCount;
  const endCall = () => { callRef.current?.end(); callRef.current = null; setCall(null); };
  useEffect(() => () => { callRef.current?.end(); callRef.current = null; }, []);
  const startCall = async () => {
    if (callRef.current) return;
    window.speechSynthesis?.cancel();
    setCall({ phase: 'connecting' });
    try {
      const tools: RealtimeTool[] = [
        { name: 'ask_lykke', description: 'Do anything on the deck or answer anything about it: build a deck, change one page or all of them, add or move pages, change the look, check a figure, answer a question. request: what the person asked, in their own words, complete, every detail they gave. It returns her written reply once the deck has changed.',
          parameters: { type: 'object', properties: { request: { type: 'string' } }, required: ['request'] },
          execute: async (a) => {
            for (let k = 0; inFlightRef.current && k < 360; k++) await new Promise((r) => setTimeout(r, 500));
            patchCall({ phase: 'working' });
            const reply = await sendRef.current!(String(a.request || ''), { voice: true });
            patchCall({ phase: 'listening' });
            return { ok: true, reply: reply.slice(0, 1800), page_on_screen: indexRefCall.current + 1, pages: pagesRefCall.current };
          } },
        { name: 'go_to_page', silent: true, description: 'Show a page (1-based). Say nothing after it.', parameters: { type: 'object', properties: { page: { type: 'integer' } }, required: ['page'] },
          execute: async (a) => { const n = Math.max(1, pagesRefCall.current); const i = Math.max(0, Math.min(n - 1, (Number(a.page) || 1) - 1)); onEvent({ event: 'go_to', index: i }); return { ok: true, page_on_screen: i + 1, pages: n }; } },
      ];
      const instructionsFor = (userName: string | null) => `You are Lykke, the deck designer at Beatly, on a voice call with ${userName || 'a colleague'} while they look at their deck in the editor${brandName ? ` (brand: ${brandName})` : ''}. The deck has ${pageCount} page(s); page ${index + 1} is on screen.
Talk like a person on a call: short turns (one or two sentences), warm, direct, the person's language.
For ANY change or question about the deck, call ask_lykke with their request in their own words, complete: never paraphrase details away. Say one short line while it works ("On it"). When it returns, say in one or two sentences what changed or the answer, from its reply: never read lists, sources or long numbers aloud; the details are in the chat.
go_to_page shows a page. Never invent figures. If something is unclear, ask one short question.`;
      // The calm line when the voice session stopped the call (its minutes ran out): it stays after the call ends.
      let overLine: string | null = null;
      const rc = new RealtimeCall(tools, {
        onStatus: (st) => {
          if (st === 'ended') { callRef.current = null; setCall(overLine ? { phase: 'error', error: overLine } : null); }
          else if (st === 'error') patchCall({ phase: 'error' });
          else if (st === 'listening') setCall((c) => (c && c.phase !== 'working' ? { ...c, phase: 'listening' } : c));
        },
        onTranscript: (role, text, final) => { if (role === 'user') patchCall({ heard: text }); else patchCall({ caption: final ? '' : text }); },
        onAudio: (playing) => setCall((c) => (c && c.phase !== 'working' ? { ...c, phase: playing ? 'speaking' : 'listening' } : c)),
        onError: (m) => patchCall({ phase: 'error', error: m }),
        // Her minutes for this call ran out (or the server ended it): the call ends ('ended' above) with this line.
        onTimeUp: (m) => { overLine = m; },
      });
      callRef.current = rc;
      // The offer goes to the mint route: the server connects the call and keeps its voice session (voiceSession.ts).
      await rc.connect((sdp) => coworkApi.presentations.presentSession(presentationId, { mode: 'build', sdp }), undefined, (secret) => ({ instructions: instructionsFor(secret.userName), voice: secret.voice, opening: `Say one short line: you're here, page ${index + 1} is on screen, and they can just tell you what they want.` }));
      if (callRef.current === rc && rc.active) patchCall({ phase: 'listening' });
    } catch (e) {
      callRef.current?.end(); callRef.current = null;
      const m = (e as Error).message || '';
      setCall({ phase: 'error', error: voiceRefusalText(e) ?? (/permission|NotAllowed/i.test(m) ? 'Microphone access is needed for a call: allow it in the browser and try again.' : 'The call could not start. Try again in a moment.') });
    }
  };

  sendRef.current = send;
  const icon = 'inline-flex h-7 w-7 items-center justify-center rounded-md text-[#78716c] hover:bg-[#f5f5f4] hover:text-[#1c1917]';
  return (
    <div className="flex h-full min-h-0 flex-col bg-[#fcfcfb]">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="flex flex-col gap-5">
          {!turns.length && (
            <div className="pt-6">
              <div className="flex items-center gap-2 text-[#be43ff]"><Sparkle className="h-5 w-5" /></div>
              <div className="mt-3 text-[20px] font-medium leading-snug text-[#1c1917]">{empty ? 'What deck do you need?' : 'What should change?'}</div>
              <div className="mt-1.5 text-[13.5px] leading-relaxed text-[#78716c]">
                {empty
                  ? `Say it like you would to a colleague, or call me. I use ${brandName ? `${brandName}'s` : 'the brand\'s'} campaigns, results and Brand OS and build the whole deck.`
                  : 'Anything: one page or all of them, new pages, order, look, a question. Click a page to edit it by hand.'}
              </div>
            </div>
          )}
          {turns.map((t, i) => (
            t.role === 'user'
              ? (
                <div key={i} className="flex flex-col items-end gap-1.5">
                  {!!t.pictures?.length && (
                    <div className="flex max-w-[85%] flex-wrap justify-end gap-1.5">
                      {t.pictures.slice(0, 8).map((u) => <img key={u} src={u} alt="" className="h-14 w-14 rounded-lg border border-[#e7e5e4] bg-white object-contain" />)}
                      {t.pictures.length > 8 && <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-[#f0efec] text-[12px] text-[#78716c]">+{t.pictures.length - 8}</span>}
                    </div>
                  )}
                  <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[#f0efec] px-3.5 py-2 text-[14px] leading-relaxed text-[#1c1917]">
                    {t.voice && <Phone className="mr-1.5 inline h-3 w-3 -translate-y-px text-[#a8a29e]" aria-label="Said on a call" />}{t.text}
                  </div>
                  {t.marked && <span className="text-[11px] text-[#a8a29e]">about the part marked on page {t.marked}</span>}
                </div>
              )
              : (
                <div key={i} className="group">
                  <div className="text-[14.5px] leading-[1.65] text-[#292524]">{t.text ? <Said text={t.text} /> : (busy && i === turns.length - 1 ? null : '…')}</div>
                  {t.text && !(busy && i === turns.length - 1) && (
                    <div className={`mt-1.5 flex items-center gap-0.5 ${i === turns.length - 1 ? '' : 'opacity-0 transition-opacity group-hover:opacity-100'}`}>
                      <button className={icon} onClick={() => copy(i, t.text)} title="Copy" aria-label="Copy">{copied === i ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}</button>
                      <button className={icon} onClick={() => readAloud(i, t.text)} title={speaking === i ? 'Stop reading' : 'Read aloud'} aria-label="Read aloud">{speaking === i ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}</button>
                      {!busy && lastAsk(i) && <button className={icon} onClick={() => void send(lastAsk(i))} title="Ask again" aria-label="Ask again"><RotateCcw className="h-3.5 w-3.5" /></button>}
                    </div>
                  )}
                </div>
              )
          ))}
          {!busy && ready && (
            <div className={`rounded-xl border px-3.5 py-2.5 ${readiness?.clean ? 'border-[#abefc6] bg-[#f6fef9]' : 'border-[#fedf89] bg-[#fffcf5]'}`}>
              <div className="flex items-center gap-2">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold text-white ${readiness?.clean ? 'bg-[#17b26a]' : 'bg-[#f79009]'}`}>{readiness?.clean ? '✓' : readiness?.running ? '…' : '!'}</span>
                <span className={`text-[13px] font-semibold ${readiness?.clean ? 'text-[#085d3a]' : 'text-[#93370d]'}`}>
                  {KIND_WORD[ready.kind || ''] || 'Deck'} · {ready.pages} pages · {readiness?.running ? 'verifying the figures' : readiness?.open ? `${readiness.open} figure${readiness.open === 1 ? '' : 's'} not verified` : readiness?.clean ? `verified${readiness.verified ? ` · ${readiness.verified} figures` : ''}` : 'built'}
                </span>
              </div>
              <div className="mt-2 flex gap-1.5">
                {readiness && !readiness.running && readiness.open > 0 && onReview
                  ? <button onClick={onReview} className="rounded-full bg-[#1c1917] px-3 py-1 text-[12px] font-medium text-white hover:bg-[#292524]">Review {readiness.open} {readiness.open === 1 ? 'check' : 'checks'}</button>
                  : onShare && <button onClick={onShare} disabled={!!readiness?.running} className="rounded-full bg-[#1c1917] px-3 py-1 text-[12px] font-medium text-white hover:bg-[#292524] disabled:opacity-50">Share with client</button>}
                <button onClick={() => onEvent({ event: 'present' })} className="rounded-full border border-[#d6d3d1] bg-white px-3 py-1 text-[12px] font-medium text-[#44403c] hover:bg-[#fafaf9]">Present</button>
              </div>
            </div>
          )}
          {!busy && decisions.length > 0 && (
            <div className="flex flex-col gap-2">
              {decisions.map((d) => (
                <div key={d.question} className="rounded-xl border border-[#e7e5e4] bg-white px-3.5 py-2.5">
                  <div className="text-[13.5px] text-[#292524]">{d.question}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {/* The first option is what the deck already does: tapping it only closes the question. */}
                    {d.options.map((o, k) => k === 0
                      ? <button key={o} onClick={() => setDecisions((all) => all.filter((x) => x !== d))} className="rounded-full bg-[#1c1917] px-2.5 py-1 text-[12px] font-medium text-white hover:bg-[#292524]" title="The deck already uses this">✓ {o}</button>
                      : <button key={o} onClick={() => void send(o)} className="rounded-full border border-[#d6d3d1] bg-white px-2.5 py-1 text-[12px] font-medium text-[#44403c] hover:bg-[#fafaf9]">{o}</button>)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {busy && (
            <div className="flex items-center gap-2 text-[13px] text-[#78716c]" aria-live="polite">
              <Sparkle className="h-4 w-4 animate-spin text-[#be43ff] [animation-duration:2.4s]" /> {status || 'Thinking'}
            </div>
          )}
        </div>
      </div>

      <AgentComposer
        ref={inputRef}
        className="mx-3 mb-3"
        agentName="Lykke"
        busy={busy}
        onStop={() => { void coworkApi.presentations.chatStop(presentationId).catch(() => undefined); setStatus('Stopping…'); }}
        accept="image/*"
        acceptFile={(f) => /^image\//.test(f.type)}
        maxFiles={MAX_PICS}
        upload={uploadPictures}
        placeholder={mark ? 'What should change in the marked part?' : empty ? 'Describe the deck you need' : 'Reply to Lykke'}
        chips={mark ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#f4ebff] py-0.5 pl-2 pr-1 text-[12px] font-medium text-[#7a1fbf]">
            <MessageSquarePlus className="h-3.5 w-3.5" />{mark.label}
            <button onClick={() => onClearMark?.()} className="rounded-full p-0.5 hover:bg-[#e9d7fe]" title="Remove the mark" aria-label="Remove the mark"><X className="h-3 w-3" /></button>
          </span>
        ) : undefined}
        menu={onStartMark && !empty ? [{ icon: MessageSquarePlus, label: 'Mark part of the page', hint: 'Drag over what Lykke should change', run: onStartMark }] : []}
        onSend={(text, files) => { if (busy) return false; void send(text, { box: true, pictures: files.filter((f) => f.url).map((f) => ({ url: f.url!, name: f.name })) }); }}
        call={{
          phase: !call ? 'off' : (call.phase as CallPhase),
          muted: call?.muted,
          caption: call ? (call.error || call.caption || (call.heard ? `“${call.heard}”` : call.phase === 'working' ? status : 'Say what you want: she changes the deck as you talk.')) : undefined,
          start: () => void startCall(),
          end: endCall,
          mute: (m) => { callRef.current?.setMuted(m); patchCall({ muted: m }); },
        }}
      />
    </div>
  );
}
